import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { ChevronDown, ChevronUp } from "lucide-react"
import { Chat } from "@/reducers/chatsSlice"
import { Message } from "@/reducers/messagesSlice"
import { MessageOptions } from "@/reducers/chatsSliceThunks"
import useChatInfo from "@/hooks/useChatInfo"
import { cn } from "@/lib/utils"
import MessageRow from "@/components/chat/MessageRow"
import ChatInput from "@/components/ChatInput"
import { useAppSelector } from "@/reducers/hooks"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"

/** Anything this close to the end still counts as "at the newest message". */
const BOTTOM_THRESHOLD_PX = 32

type Props = {
  chat: Chat
  /** Everything the driver sees, Slick AI included — filtered by the caller. */
  messages: Message[]
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
  /** Collapsible-card chrome (default). Omit `expanded`/`onExpandedChange`
   * and pass `bare` to drop the card + header and fill the parent instead —
   * used inside the tabbed widget panel. */
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  bare?: boolean
}

/**
 * The driver-facing conversation, pulled out of the centre panel (which now
 * shows only the Slick AI / Agent thread). Renders a non-virtualised list of
 * `MessageRow`s — fine for a sidebar-sized thread — and inherits
 * `ChatHistoryContext` / `DriverContext` / `AudioPlaybackContext` from
 * `ChatHistory`, which wraps it.
 */
const DriverChatCard = ({
  chat,
  messages,
  onSend,
  expanded = true,
  onExpandedChange,
  bare = false,
}: Props) => {
  const { t } = useTranslation()
  const { chatTitle, driverAName: driverName } = useChatInfo(chat)
  const accountChat = useAppSelector(selectAccountChatByJid(chat.jid))
  // "Send to Gosia" beats the full name on a button this narrow. The room's
  // own title starts with a plate when no driver is resolved, so the name has
  // to come from the driver — or from nobody.
  const firstName = driverName.split(" ")[0]
  const sendLabel = firstName
    ? t("chat.driverChat.sendTo", { name: firstName })
    : t("chat.driverChat.sendToDriver")
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  // Follow the newest message until the dispatcher scrolls up to read history.
  const stickToBottomRef = useRef(true)
  const open = bare || expanded

  // Another room is another conversation: whatever history the dispatcher had
  // scrolled back to here does not carry over. Declared first so it runs
  // before the pinning effect below.
  useEffect(() => {
    stickToBottomRef.current = true
  }, [chat.jid])

  // Pin the thread to the newest message. A one-shot scroll is not enough:
  // the pane can be laid out after the first paint (it starts behind another
  // tab) and rows keep growing as images and maps load, which leaves the
  // viewport parked weeks back in the history. The observer re-pins on every
  // size change of the scroller and of its content.
  useEffect(() => {
    if (!open) return
    const el = scrollRef.current
    const content = contentRef.current
    if (!el) return

    // Last position the dispatcher was looking at, pins included.
    let lastTop = el.scrollTop

    const pinToBottom = () => {
      if (!stickToBottomRef.current) return
      el.scrollTop = el.scrollHeight
      lastTop = el.scrollTop
    }

    // Only the dispatcher moving the view *up* stops the following. Growing
    // rows leave the position untouched, and the scroll event the browser
    // queues after a pin can land a frame later, once the thread is already
    // taller — reading the distance alone would read that as "scrolled up".
    const onScroll = () => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight
      const atBottom = distanceFromBottom <= BOTTOM_THRESHOLD_PX
      if (el.scrollTop < lastTop) stickToBottomRef.current = atBottom
      else if (atBottom) stickToBottomRef.current = true
      lastTop = el.scrollTop
    }

    pinToBottom()

    const observer = new ResizeObserver(pinToBottom)
    observer.observe(el)
    if (content) observer.observe(content)
    el.addEventListener("scroll", onScroll)
    return () => {
      observer.disconnect()
      el.removeEventListener("scroll", onScroll)
    }
  }, [open, chat.jid, messages.length])

  const body = (
    <>
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 scrollbar-borderless"
      >
        <div ref={contentRef}>
          {messages.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t("chat.driverChat.empty")}
            </div>
          ) : (
            messages.map((message, index) => (
              <MessageRow
                key={`${message.id}-${index}`}
                previousRow={messages[index - 1]}
                row={message}
                nextRow={messages[index + 1]}
                accountChat={accountChat}
                isDriverRoomChat
                composerSurface="driver"
              />
            ))
          )}
        </div>
      </div>
      <div className="border-t border-gray-100 px-3 py-3 dark:border-gray-800">
        <ChatInput
          key={`${chat.jid}-driver-widget`}
          chat={chat}
          onSend={onSend}
          autoFocus={false}
          sendLabel={sendLabel}
        />
      </div>
    </>
  )

  if (bare) {
    return <div className="flex h-full min-h-0 flex-col">{body}</div>
  }

  return (
    <div
      className={cn(
        // Always shrink-0: when expanded the card is tall (message list +
        // composer) and the widget column scrolls rather than squeezing it.
        "flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-[0_4px_20px_rgba(0,0,0,0.04)]",
      )}
    >
      <div
        className="flex h-12 shrink-0 cursor-pointer items-center justify-between px-4"
        onClick={() => onExpandedChange?.(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            onExpandedChange?.(!expanded)
          }
        }}
        aria-expanded={expanded}
        title={expanded ? t("chat.driverChat.hide") : t("chat.driverChat.show")}
      >
        <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <span className="truncate">
            {t("chat.driverChat.title", { title: chatTitle })}
          </span>
          {messages.length > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-lg bg-gray-200 px-1 text-[10px] font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              {messages.length}
            </span>
          )}
        </h2>
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        )}
      </div>
      {expanded && (
        <div className="flex max-h-[45vh] min-h-[8rem] flex-col border-t border-gray-100 dark:border-gray-800">
          {body}
        </div>
      )}
    </div>
  )
}

export default DriverChatCard
